import {
  ObjectAssignedEnum,
  ValidationById,
  ValidationResponse,
  ValidationTypes,
} from "types/validation";

export interface GroupedValidations {
  [id: string]: ValidationResponse[];
}

export const GroupAndSortValidations = (
  validationsById: ValidationById
): GroupedValidations => {
  //Some helper functions
  const isProperty = (arr: ValidationResponse[]): boolean =>
    arr[0]?.objectAssigned === ObjectAssignedEnum.PROPERTY;

  const getPropName = (arr: ValidationResponse[]): string =>
    (arr[0]?.objectInfo as any)?.Name?.toString?.() ?? ""; // adjust if you have a typed objectInfo

  // 1 )Group validations by their objects.
  const grouped: GroupedValidations = {};
  Object.values(validationsById).forEach((validation) => {
    const objectId = validation.objectId;
    if (!grouped[objectId]) {
      grouped[objectId] = [];
    }
    grouped[objectId].push(validation);
  });

  // 2) Sort keys using the first item in each group as the representative
  const keys = Object.keys(grouped);
  keys.sort((a, b) => {
    const A = grouped[a];
    const B = grouped[b];

    const aIsProp = isProperty(A);
    const bIsProp = isProperty(B);

    if (aIsProp && !bIsProp) return -1;
    if (!aIsProp && bIsProp) return 1;

    if (aIsProp && bIsProp) {
      const nameA = getPropName(A).toLowerCase();
      const nameB = getPropName(B).toLowerCase();
      return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
    }

    // both rule tables → simple string compare is fine for Mongo IDs
    return a.localeCompare(b);
  });

  // 3) Rebuild object in the sorted order to preserve iteration order for Object.entries(...)
  const ordered: GroupedValidations = {};
  for (const k of keys) {
    ordered[k] = grouped[k];
  }

  return ordered;
};

export const FilterGroupedValidations = (
  groupedValidations: GroupedValidations,
  validationFilters: Record<ValidationTypes, boolean>
): GroupedValidations => {
  const filteredResult: GroupedValidations = {};

  // Iterate through each validation group.
  for (const groupId of Object.keys(groupedValidations)) {
    const filteredGroup: ValidationResponse[] = [];

    for (const groupMember of groupedValidations[groupId]) {
      //  Allow all types that are not explicitly filtered out back into group.
      if (
        !(groupMember.type in validationFilters) ||
        validationFilters[groupMember.type]
      ) {
        filteredGroup.push(groupMember);
      }
    }

    // Only add groups that aren't empty back.
    if (filteredGroup.length > 0) {
      filteredResult[groupId] = filteredGroup;
    }
  }

  return filteredResult;
};
